import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, HelpCircle, Zap, Package, Star, Shield, Heart, MessageCircle, Sparkles, Mail, Send, ArrowRight } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { EXTERNAL_LINKS } from '../config';
import './FAQs.css';

const FAQs = () => {
  const { t, language } = useLanguage();
  const [openFAQ, setOpenFAQ] = useState(null);
  const isEN = language === 'en';

  /* ── Contact form state ── */
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch('https://formsubmit.co/ajax/info@whatalab.io', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: form.message,
          _subject: `WhataLab Contact — ${form.name}`,
          _captcha: 'false',
        }),
      });
      const data = await res.json();
      if (data.success === 'true' || data.success === true) {
        setStatus('sent');
        setForm({ name: '', email: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const faqsEN = [
    { id: 1, icon: <HelpCircle size={20} />, question: "What is Whatalab?", answer: "Whatalab is a lab of tools built on top of Hedera, designed to empower NFT holders. We offer practical solutions and exclusive experiences for Web3 communities, already created. Our mission: to facilitate, automate and professionalize the NFT experience in the Hedera ecosystem." },
    { id: 2, icon: <Zap size={20} />, question: "What is a WATT?", answer: "A WATT is the initial key to the Whatalab lab. It was the first NFT to grant early access to the tools and community. Although they sold out, they are still a proof of your faith in the project and can influence future benefits, such as boosted packs..." },
    { id: 3, icon: <Package size={20} />, question: "What is an Early Pack?", answer: "Early Packs were limited packages with defined benefits. They include management for communities, advanced verification, automatic lists, boosters and much more. They are no longer for sale, but their holders enjoy unique and cumulative benefits." },
    { id: 4, icon: <Star size={20} />, question: "What is a WhataKit?", answer: "The WhataKit is the new premium pass to access suite of tools. Each NFT gives access to the first Hedera NFTs Sniper, a scanner, an orderbook & a claimdrop tools. Your NFT is your access pass!" },
    { id: 5, icon: <Shield size={20} />, question: "Can I access without an NFT?", answer: "Not for now. Access to tools and benefits is directly linked to NFT ownership. Without one of this NFTs, you will not be able to use the tools." },
    { id: 6, icon: <Heart size={20} />, question: "What is Whatalab's Philosophy?", answer: "Our philosophy transcends technology. We believe in visionary communities that together transform the future of Web3. Community formed by pioneers and builders who see beyond the obvious, decentralization as technology and as a transformative culture, we do not seek users, we build lasting partnerships based on shared value." },
  ];

  const faqsES = [
    { id: 1, icon: <HelpCircle size={20} />, question: "¿Qué es Whatalab?", answer: "Whatalab es un laboratorio de herramientas construido sobre Hedera, diseñado para potenciar a los holders de NFT. Ofrecemos soluciones prácticas y experiencias exclusivas para comunidades Web3 ya creadas. Nuestra misión: facilitar, automatizar y profesionalizar la experiencia NFT en el ecosistema Hedera." },
    { id: 2, icon: <Zap size={20} />, question: "¿Qué es un WATT?", answer: "Un WATT es la llave inicial al laboratorio Whatalab. Fue el primer NFT en otorgar acceso anticipado a las herramientas y la comunidad. Aunque se agotaron, siguen siendo una prueba de tu fe en el proyecto y pueden influir en beneficios futuros, como paquetes potenciados..." },
    { id: 3, icon: <Package size={20} />, question: "¿Qué es un Early Pack?", answer: "Los Early Packs fueron paquetes limitados con beneficios definidos. Incluyen gestión para comunidades, verificación avanzada, listas automáticas, boosters y mucho más. Ya no están a la venta, pero sus holders disfrutan de beneficios únicos y acumulativos." },
    { id: 4, icon: <Star size={20} />, question: "¿Qué es un WhataKit?", answer: "El WhataKit es el nuevo pase premium para acceder a la suite de herramientas. Cada NFT da acceso al primer Sniper de NFTs de Hedera, un scanner, un orderbook y herramientas de claimdrop. ¡Tu NFT es tu pase de acceso!" },
    { id: 5, icon: <Shield size={20} />, question: "¿Puedo acceder sin un NFT?", answer: "No por ahora. El acceso a herramientas y beneficios está directamente vinculado a la propiedad de NFT. Sin uno de estos NFTs, no podrás usar las herramientas." },
    { id: 6, icon: <Heart size={20} />, question: "¿Cuál es la filosofía de Whatalab?", answer: "Nuestra filosofía trasciende la tecnología. Creemos en comunidades visionarias que juntas transforman el futuro de Web3. Comunidad formada por pioneros y constructores que ven más allá de lo obvio, descentralización como tecnología y como cultura transformadora, no buscamos usuarios, construimos asociaciones duraderas basadas en valor compartido." },
  ];

  const faqs = language === 'es' ? faqsES : faqsEN;
  const toggleFAQ = (id) => setOpenFAQ(openFAQ === id ? null : id);

  return (
    <section className="faqs section" id="faqs">
      <div className="container">

        {/* ── Header — mismo patrón que WhataTeam, centrado ── */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          <div className="ps-header ps-header--center">
            <div className="ps-eyebrow">
              <span className="ps-eyebrow-dot" />
              {isEN ? 'got questions?' : '¿tienes preguntas?'}
            </div>
            <div className="ps-header-row">
              <h2 className="ps-title">
                {isEN
                  ? <><span className="ps-title-accent">Frequently</span><br />Asked Questions.</>
                  : <><span className="ps-title-accent">Preguntas</span><br />Frecuentes.</>
                }
              </h2>
            </div>
            <p className="ps-subtitle">{t('faqs.subtitle')}</p>
          </div>
        </motion.div>

        {/* ── FAQ accordion grid ── */}
        <div className="faqs-grid">
          <div className="faqs-left">
            {faqs.slice(0, Math.ceil(faqs.length / 2)).map((faq, index) => (
              <motion.div key={faq.id} className="faq-item" initial={{ opacity: 0, x: -18 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.35, delay: index * 0.1 }} viewport={{ once: true, amount: 0.1 }}>
                <button className={`faq-question ${openFAQ === faq.id ? 'active' : ''}`} onClick={() => toggleFAQ(faq.id)}>
                  <div className="faq-question-content">
                    <div className="faq-icon">{faq.icon}</div>
                    <span className="faq-number">{faq.id}.</span>
                    <span className="faq-text">{faq.question}</span>
                  </div>
                  <ChevronDown className={`faq-chevron ${openFAQ === faq.id ? 'rotated' : ''}`} size={20} />
                </button>
                <motion.div className="faq-answer" initial={false} animate={{ height: openFAQ === faq.id ? 'auto' : 0, opacity: openFAQ === faq.id ? 1 : 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }}>
                  <div className="faq-answer-content">{faq.answer}</div>
                </motion.div>
              </motion.div>
            ))}
          </div>

          <div className="faqs-right">
            {faqs.slice(Math.ceil(faqs.length / 2)).map((faq, index) => (
              <motion.div key={faq.id} className="faq-item" initial={{ opacity: 0, x: 18 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.35, delay: index * 0.1 }} viewport={{ once: true, amount: 0.1 }}>
                <button className={`faq-question ${openFAQ === faq.id ? 'active' : ''}`} onClick={() => toggleFAQ(faq.id)}>
                  <div className="faq-question-content">
                    <div className="faq-icon">{faq.icon}</div>
                    <span className="faq-number">{faq.id}.</span>
                    <span className="faq-text">{faq.question}</span>
                  </div>
                  <ChevronDown className={`faq-chevron ${openFAQ === faq.id ? 'rotated' : ''}`} size={20} />
                </button>
                <motion.div className="faq-answer" initial={false} animate={{ height: openFAQ === faq.id ? 'auto' : 0, opacity: openFAQ === faq.id ? 1 : 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }}>
                  <div className="faq-answer-content">{faq.answer}</div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── Contact section ── */}
        <motion.div
          className="contact-section" id="contact"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          viewport={{ once: true, amount: 0.1 }}
        >
          {/* Left col */}
          <div className="contact-left">
            <span className="contact-badge">
              <span className="contact-badge-dot" />
              {isEN ? 'Contact' : 'Contacto'}
            </span>

            <h2 className="contact-title">
              {isEN ? "Let's Build\nIntelligent Things" : "Construyamos\nCosas Inteligentes"}
            </h2>

            <div className="contact-info-items">
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <Mail size={20} />
                </div>
                <div>
                  <p className="contact-info-label">{isEN ? 'E-mail address' : 'Correo electrónico'}</p>
                  <a href="mailto:info@whatalab.io" className="contact-info-value">info@whatalab.io</a>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">
                  {/* Discord icon */}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                  </svg>
                </div>
                <div>
                  <p className="contact-info-label">Discord</p>
                  <a href={EXTERNAL_LINKS.DISCORD} target="_blank" rel="noopener noreferrer" className="contact-info-value">discord.gg/WhataLab</a>
                </div>
              </div>
            </div>

            {/* Open a ticket CTA */}
            <a
              href={EXTERNAL_LINKS.DISCORD}
              target="_blank"
              rel="noopener noreferrer"
              className="contact-ticket-btn"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
              </svg>
              {isEN ? 'Open a ticket' : 'Abrir un ticket'}
            </a>
          </div>

          {/* Right col — form */}
          <div className="contact-right">
            <div className="contact-form-card">
              <h3 className="contact-form-title">
                {isEN ? 'Fill this form below' : 'Rellena el formulario'}
              </h3>

              {status === 'sent' ? (
                <div className="contact-success">
                  <Send size={40} strokeWidth={1.5} />
                  <p>{isEN ? 'Message sent! We\'ll get back to you soon.' : '¡Mensaje enviado! Te responderemos pronto.'}</p>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="contact-field">
                    <label>{isEN ? 'Your Name' : 'Tu Nombre'}</label>
                    <input
                      type="text"
                      placeholder={isEN ? 'Enter your full name' : 'Escribe tu nombre completo'}
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="contact-field">
                    <label>{isEN ? 'Your Email' : 'Tu Email'}</label>
                    <input
                      type="email"
                      placeholder={isEN ? 'Enter your e-mail' : 'Escribe tu e-mail'}
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      required
                    />
                  </div>

                  <div className="contact-field">
                    <label>{isEN ? 'More About The Project' : 'Más sobre el proyecto'}</label>
                    <textarea
                      placeholder={isEN ? 'Tell us about your project or question...' : 'Cuéntanos sobre tu proyecto o pregunta...'}
                      rows={4}
                      value={form.message}
                      onChange={e => setForm({ ...form, message: e.target.value })}
                      required
                    />
                  </div>

                  {status === 'error' && (
                    <p className="contact-error">
                      {isEN ? 'Something went wrong. Please try again.' : 'Algo salió mal. Inténtalo de nuevo.'}
                    </p>
                  )}

                  <button type="submit" className="contact-submit" disabled={status === 'sending'}>
                    {status === 'sending'
                      ? (isEN ? 'Sending...' : 'Enviando...')
                      : (isEN ? 'Submit Message' : 'Enviar Mensaje')
                    }
                  </button>
                </form>
              )}
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default FAQs;
